export const COMPARE_CONDITION_EQUAL = "EQUAL";
export const COMPARE_CONDITION_NOT_EQUAL = "NOT_EQUAL";

export const COMPARE_CONDITIONS = [
  { value: COMPARE_CONDITION_EQUAL, symbol: "=" },
  { value: COMPARE_CONDITION_NOT_EQUAL, symbol: "≠" },
];

/**
 * Field types that carry a single typed-in value two fields can meaningfully be
 * compared on. Everything else -- option pickers, media, device capture -- is
 * left out: their "value" is a selection or a URL/coordinate blob, so equality
 * reads as noise.
 *
 * Keep the `compareValidationEligibleTypes` string in Translations.js in step
 * with this list -- it names these types to the admin.
 */
export const COMPARE_ELIGIBLE_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "email",
  "date",
  "time",
];

export function isCompareEligibleField(field) {
  if (!field?.fieldType) return false;
  return COMPARE_ELIGIBLE_FIELD_TYPES.includes(field.fieldType);
}

/** Depth-first list of every field on the canvas, nested radio children included. */
export function flattenWorkflowFields(items) {
  if (!Array.isArray(items)) return [];
  return items.reduce((acc, item) => {
    if (!item) return acc;
    acc.push(item);
    if (item.radioInputValues?.length) {
      acc.push(...flattenWorkflowFields(item.radioInputValues));
    }
    return acc;
  }, []);
}

/** Every field the given one may be compared against (itself excluded). */
export function getCompareTargetFields(items, selfUniqueKey) {
  return flattenWorkflowFields(items).filter(
    (field) =>
      field.uniqueKey !== selfUniqueKey && isCompareEligibleField(field),
  );
}

export function getCompareValidations(items, uniqueKey) {
  const field = flattenWorkflowFields(items).find(
    (item) => item.uniqueKey === uniqueKey,
  );
  return Array.isArray(field?.compareValidations)
    ? field.compareValidations
    : [];
}

function normalizeCompareValue(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.map(normalizeCompareValue).join(",");
  return String(value).trim();
}

/**
 * True when the two values satisfy the rule. Numeric strings are compared as
 * numbers so `007` and `7` don't read as different.
 */
export function evaluateCompareValidation(condition, sourceValue, secondaryValue) {
  const a = normalizeCompareValue(sourceValue);
  const b = normalizeCompareValue(secondaryValue);

  const numericA = Number(a);
  const numericB = Number(b);
  const bothNumeric =
    a !== "" && b !== "" && !Number.isNaN(numericA) && !Number.isNaN(numericB);

  const isEqual = bothNumeric ? numericA === numericB : a === b;

  return condition === COMPARE_CONDITION_NOT_EQUAL ? !isEqual : isEqual;
}

export function compareConditionSymbol(condition) {
  return (
    COMPARE_CONDITIONS.find((item) => item.value === condition)?.symbol ??
    COMPARE_CONDITIONS[0].symbol
  );
}

/** Adds the rule, or replaces the existing one against the same second field. */
export function upsertCompareValidation(compareValidations, rule) {
  const list = Array.isArray(compareValidations) ? [...compareValidations] : [];
  const index = list.findIndex(
    (item) => item.secondaryUniqueKey === rule.secondaryUniqueKey,
  );
  if (index > -1) {
    list[index] = rule;
  } else {
    list.push(rule);
  }
  return list;
}

function mapCompareValidations(items, mapper) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item || typeof item !== "object") return item;

    const next = { ...item };
    if (Array.isArray(next.compareValidations)) {
      const rules = next.compareValidations.map(mapper).filter(Boolean);
      if (rules.length) {
        next.compareValidations = rules;
      } else {
        delete next.compareValidations;
      }
    }
    if (next.radioInputValues?.length) {
      next.radioInputValues = mapCompareValidations(
        next.radioInputValues,
        mapper,
      );
    }
    return next;
  });
}

/**
 * Drops rules that point at a field which is no longer on the canvas -- called
 * when a field is deleted, so the remaining fields don't keep a comparison
 * against something the admin can't see any more.
 */
export function removeCompareValidationsTargeting(items, removedUniqueKeys) {
  const removed = new Set(removedUniqueKeys || []);
  if (removed.size === 0) return items;
  return mapCompareValidations(items, (rule) =>
    removed.has(rule?.secondaryUniqueKey) ? null : rule,
  );
}
